import t from '../translate';

const OK = 200;
const CREATED = 201;
const ACCEPTED = 202;
const BAD_REQUEST = 400;
const NOT_AUTHORIZED = 401;
const FORBIDDEN = 403;
const NOT_FOUND = 404;
const INVALID_METHOD = 405;
const CONFLICT = 409;
const PRECONDITION_FAILED = 412;
const EXPECTATION_FAILED = 417;
const LOCKED = 423;
const TOO_MANY_REQUESTS = 429;
const INTERNAL_SERVER_ERROR = 500;
const BAD_GATEWAY = 502;
const SERVICE_UNAVAILABLE = 503;
const REQUEST_TIMEOUT = 504;

export const HTTP_STATUS_CODES = {
  OK,
  CREATED,
  ACCEPTED,
  BAD_REQUEST,
  NOT_AUTHORIZED,
  NOT_FOUND,
  INVALID_METHOD,
  CONFLICT,
  PRECONDITION_FAILED,
  LOCKED,
  EXPECTATION_FAILED,
  FORBIDDEN,
  TOO_MANY_REQUESTS,
  INTERNAL_SERVER_ERROR,
  BAD_GATEWAY,
  SERVICE_UNAVAILABLE,
  REQUEST_TIMEOUT
};

export class HTTP {
  static get(url, silentErrorObj) {
    return HTTP.perform('get', url, null, null, silentErrorObj);
  }

  static post(url, params, responseType = 'json') {
    return HTTP.perform('post', url, params, responseType);
  }

  static put(url, params) {
    return HTTP.perform('put', url, params);
  }

  static delete(url, params) {
    return HTTP.perform('delete', url, params);
  }

  static returnResponse(responseType, response) {
    switch (responseType) {
      case 'blob':
        return response.blob();
      case 'text':
        return response.text();
      case 'json':
      default:
        return response.json();
    }
  }

  static perform(method, url, params, responseType = 'json', silentErrorArr) {
    return fetch(url, {
      method,
      ...(params
        ? {
            body: JSON.stringify(params),
            headers: {
              'Content-Type': 'application/json;charset=utf-8'
            }
          }
        : {}),
      credentials: 'same-origin'
    }).then(response => {
      if (response instanceof Error) {
        return Promise.reject(response);
      }

      if (silentErrorArr && silentErrorArr.includes(response.status)) {
        return HTTP.returnResponse(responseType, response);
      }

      if (response.status === REQUEST_TIMEOUT || response.status === INVALID_METHOD) {
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject({});
      }

      if (response.status >= BAD_REQUEST) {
        return response.json().then(json => Promise.reject(json));
      }

      return HTTP.returnResponse(responseType, response);
    });
  }
}

const convertHtmlToText = html => {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
};

const parseStringError = errorMessage => {
  const trimmed = errorMessage.trim();

  try {
    if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
      const fixedMessage = errorMessage.replace(/'/g, '"');
      const parsed = JSON.parse(fixedMessage);
      if (Array.isArray(parsed) && parsed.length > 0 && parsed[0]?.msg) {
        return parsed.map(err => err.msg).join(', ');
      }
      return parsed.msg;
    }
  } catch (e) {
    // do nothing and continue
  }
  return null;
};

/** receives either the caught error (error: {response: {data}}), or the errorData (response: {data}) from useMutation */
export const parseHttpError = (error, defaultMessage) => {
  const errData = error?.response?.data || error?.data || error;
  let errorMessage =
    errData?.error?.message ||
    errData?.errorMessage?.message || // TEMP for migration phase of FES - TODO - remove after FES code is pushed
    errData?.message ||
    errData?.errorMessage || // TEMP for migration phase of FES - TODO - remove after FES code is pushed
    errData?.error ||
    errData;

  // this is needed for the case when we receive error like this: "[{'type': 'value_error', 'loc': ['body', 'attributes', 6, 'name'], 'msg': 'Value error, Invalid name: Alina_Org@!, 'ctx': {'error': {}}}]"
  if (typeof errorMessage === 'string' && !errorMessage.includes('<html>')) {
    return parseStringError(errorMessage) || errorMessage;
  }

  const code = error?.response?.status || error?.status;
  if (code === BAD_GATEWAY || code === SERVICE_UNAVAILABLE || code === REQUEST_TIMEOUT) {
    return t('error.genericLater');
  }

  if (typeof errorMessage === 'string' && errorMessage?.includes('<html>')) {
    errorMessage = convertHtmlToText(errorMessage);
  }

  return (errorMessage === 'string' && errorMessage) || defaultMessage || t('error.generic');
};
